import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { LiaCalendar } from 'react-icons/lia';
import { getDateRange, getFormatterDateRange } from '../../routes/home/util/GetDateRangeUtil';
import { DateRangeOption } from '../../routes/home/enum/Dashboard.enum';
import { CgChevronDown } from 'react-icons/cg';
import DateSelector from './DateSelector'

interface DateRangeProps {
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
}

interface SelectedRange {
  option: DateRangeOption;
  startDate: Date | null;
  endDate: Date | null;
}

interface OptionInfo {
  label: string;
  subLabel: string;
}

const radioOptions = [
  {label: "Hoy", subLabel: "", value: DateRangeOption.TODAY},
  {label: "Esta Semana", subLabel: "De lunes a hoy", value: DateRangeOption.THIS_WEEK},
  {label: "Este Mes", subLabel: "Del primero a hoy", value: DateRangeOption.THIS_MONTH},
]

const rangeFromOption = (option: DateRangeOption): SelectedRange => ({ option, ...getDateRange(option) });

// Un rango elegido en el calendario (CUSTOM) se muestra con sus fechas en lugar de una opción.
const getOptionInfo = ({ option, startDate, endDate }: SelectedRange): OptionInfo => {
  if (option === DateRangeOption.CUSTOM) {
    const formatted = getFormatterDateRange(startDate ?? undefined, endDate ?? undefined);
    return { label: `${formatted.startDate} - ${formatted.endDate}`, subLabel: '' };
  }

  return radioOptions.find(radioOption => radioOption.value === option) ?? radioOptions[0];
};

// Si en el calendario solo se marcó el inicio, el rango aplicado es ese único día.
const completeCustomRange = ({ startDate, endDate }: SelectedRange): SelectedRange | null =>
  startDate ? { option: DateRangeOption.CUSTOM, startDate, endDate: endDate ?? startDate } : null;

const DateRangeSelector = ({ onDateRangeChange }: DateRangeProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [appliedRange, setAppliedRange] = useState<SelectedRange>(() => rangeFromOption(DateRangeOption.TODAY))
  const [draftRange, setDraftRange] = useState<SelectedRange>(appliedRange)
  const selectedOptionInfo = getOptionInfo(appliedRange)

  useEffect(() => {
    onDateRangeChange(appliedRange.startDate, appliedRange.endDate)
  }, []);

  const applyRange = (range: SelectedRange) => {
    setAppliedRange(range)
    onDateRangeChange(range.startDate, range.endDate)
  }

  const handleOpen = () => {
    setDraftRange(appliedRange)
    onOpen()
  }

  const handleOptionChange = (value: DateRangeOption) => {
    setDraftRange(rangeFromOption(value))
  }

  const handleClear = () => {
    const todayRange = rangeFromOption(DateRangeOption.TODAY)
    setDraftRange(todayRange)
    applyRange(todayRange)
  }

  const handleApply = () => {
    const range = draftRange.option === DateRangeOption.CUSTOM
      ? completeCustomRange(draftRange)
      : rangeFromOption(draftRange.option)

    if (range) {
      applyRange(range)
    }
    onClose()
  }

  const onChange = (dates: [Date | null, Date | null]) => {
    const [startDate, endDate] = dates;
    setDraftRange({ option: DateRangeOption.CUSTOM, startDate, endDate });
  };

  return <>
    <Menu  isOpen={isOpen} onClose={onClose}>
      <MenuButton p={2} borderWidth={1} borderRadius="md" onClick={handleOpen}>
        <HStack justifyItems="left">
          <LiaCalendar color="black" fontSize="1.1em"/>
          <Stack gap="0" justifyItems="left">
            <Text color="black" fontSize="m">{selectedOptionInfo.label}</Text>
            <Text color="neutral.600" fontSize="smaller">{selectedOptionInfo.subLabel || null}</Text>
          </Stack>
          <CgChevronDown fontSize="1.5em" color="black"/>
        </HStack>
      </MenuButton>
      <Portal>
        <MenuList zIndex="popover">
          <Flex>
            <Box p={2} borderRightWidth="thin" px="5">
              <RadioGroup onChange={handleOptionChange} value={draftRange.option}>
                <Stack direction="column" >
                  {
                    radioOptions.map(option => (
                      <Radio key={option.value} size="lg" height="3.5em" value={option.value} colorScheme="orange">
                        <Stack gap="0">
                          <Text color="black" fontSize="l">{option.label}</Text>
                          <Text color="neutral.600" fontSize="smaller">{option.subLabel || null}</Text>
                        </Stack>
                      </Radio>
                    ))
                  }
                </Stack>
              </RadioGroup>
            </Box>
            <DateSelector startDate={draftRange.startDate}
                          endDate={draftRange.endDate}
                          handleOnChangeDates={onChange} 
                          handleClear={handleClear} 
                          handleApply={handleApply} />
          </Flex>
        </MenuList>
      </Portal>
    </Menu>
  </>
}

export default DateRangeSelector
